import React from 'react'
import { useAppDispatch, useAppSelector } from '../../../../hooks/redux'

import SettingsSection from '../../SettingsSection'
import Edit from '../../Controls/Edit'
import Add from '../../Controls/Add'
import AddColor from '../../Controls/Add/AddColour'

import { useSettingsEdit } from '../../../../providers/SettingsEditProvider'
import { AddErrorAlert, AddSuccessAlert } from '../../../../slices/alert'
import { DEFAULT_COLORS } from '../../Controls/Add/helpers'
import {
  createUserStatus,
  deleteUserStatus,
  fetchUserStatus,
  selectUserStatus,
  updateUserStatus
} from '../../../../slices/userStatus'
import NoPermissionContainer from '../../../../components/molecules/NoPermissionContainer'

const CustomerStatusSettings = () => {
  const dispatch = useAppDispatch()
  const { loaded, loading, statuses, error } = useAppSelector(selectUserStatus)
  const { setEditObject } = useSettingsEdit()

  const [color, setColor] = React.useState(null)
  const [saving, setSaving] = React.useState(false)

  const handleDeleteStatus = React.useCallback(
    obj => {
      try {
        dispatch(deleteUserStatus(obj.id))
        dispatch(AddSuccessAlert('Changes saved'))
      } catch (err) {
        dispatch(AddErrorAlert({ err }))
        console.error(err)
      }
    },
    [dispatch]
  )

  const handleEditStatus = React.useCallback(
    async obj => {
      try {
        const editObj = {
          name: obj.name,
          colour: obj.colour?.toLowerCase() ?? ''
        }

        if (editObj.colour[0] === '#') {
          editObj.colour = editObj.colour.substring(1)
        } else if (editObj.colour === '') {
          editObj.colour = null
        }

        dispatch(updateUserStatus(obj.id, editObj))
        dispatch(AddSuccessAlert('Changes saved'))
        setEditObject(null)
      } catch (err) {
        dispatch(AddErrorAlert({ err }))
        console.error(err)
      }
    },
    [dispatch, setEditObject]
  )

  const handleAddStatus = React.useCallback(
    async name => {
      try {
        setSaving(true)

        const randomColor =
          DEFAULT_COLORS[
            Math.floor(Math.random() * DEFAULT_COLORS.length)
          ].toLowerCase()

        const addObj = { name, colour: color?.toLowerCase() ?? randomColor }

        if (addObj.colour[0] === '#') {
          addObj.colour = addObj.colour.substring(1)
        } else if (addObj.colour === '') {
          addObj.colour = null
        }

        await dispatch(createUserStatus(addObj))
        dispatch(AddSuccessAlert('Changes saved'))
        setColor(null)
      } catch (err) {
        dispatch(AddErrorAlert({ err }))
        console.error(err)
      } finally {
        setSaving(false)
      }
    },
    [color, dispatch]
  )

  React.useEffect(() => {
    if (!loaded && !loading) {
      dispatch(fetchUserStatus())
    }
  }, [dispatch, loaded, loading])

  return (
    <NoPermissionContainer skipAgentPage error={error}>
      <SettingsSection title='Statuses' loaded={loaded}>
        {statuses.map(status => (
          <Edit
            key={status.id}
            obj={status}
            objType='status'
            superType='user'
            handleDelete={handleDeleteStatus}
            handleEdit={handleEditStatus}
          />
        ))}

        <Add
          superType='user'
          type='status'
          handleAdd={handleAddStatus}
          saving={saving}
        >
          <AddColor saveColor={setColor} prevColor={color} />
        </Add>
      </SettingsSection>
    </NoPermissionContainer>
  )
}

export default CustomerStatusSettings
